import {saveEventThunk} from '~/CreateFabricInvitation/api/event';
import {history} from '~/CreateFabricInvitation/history';
import {sendEvent} from '~/CreateFabricInvitation/navigation/createSteps';
import {selectIsDirty} from '~/CreateFabricInvitation/reducers/details/selectors';
import {NotifyGuestsOverlayPromise} from '~/CreateFabricInvitation/utils/modalPromises';
import {canvasRemoveTextBordersAndIcons} from '~/common/canvas/customs/text_border';
import {HTTP_STATUS_PAYMENT_REQUIRED} from '~/common/fabric/constants';
import {UPDATE_EVENT_DETAILS} from '~/common/fabric/reducers/details/constants';
import {UPDATE_DESIGN_OPTIONS} from '~/common/fabric/reducers/fabricDesign/constants';
import {isSubscriptionPlan} from '~/common/fabric/User/utils';
import {validateEventDetails} from './validation';

const suppressGuestUpdateModal = (store) => {
  store.dispatch({
    type: UPDATE_EVENT_DETAILS,
    payload: {preventUpdateModal: true},
  });
};

const saveAndSendGuestsMessage = async (store) => {
  const state = store.getState();
  const includesPath = (path) => window.location.pathname.includes(path);

  // In edit flow, ONLY send invitations to draft guests if in Add Guests page or Review/Checkout page
  const sendInvitations = includesPath('guests');

  if (sendInvitations) {
    const sendResponse = await sendEvent(store);
    if (sendResponse?.error?.status === HTTP_STATUS_PAYMENT_REQUIRED) {
      const onPathToPreventUpdateModal = ['guests', 'confirm'].some(includesPath);
      const preventUpdateModal =
        onPathToPreventUpdateModal ||
        state?.guests?.totalDrafts > 0 ||
        state?.details?.preventUpdateModal;
      if (!preventUpdateModal) {
        try {
          await store.dispatch({
            type: UPDATE_DESIGN_OPTIONS,
            payload: {savingStep: false},
          });
          await NotifyGuestsOverlayPromise(store.dispatch);
          // Message sent or skipped
          // biome-ignore lint/correctness/noUnusedVariables: existing error
        } catch (e) {
          // Modal closed
          // biome-ignore lint/style/useThrowOnlyError: existing error
          throw null;
        }
      }
      history.replace({
        pathname: `/${state.eviteEvent.id}/review`,
        hash: history.location.hash,
        search: history.location.search,
      });
      return false;
    }
    if (sendResponse?.error) {
      if (sendResponse.error.data?.message) {
        // when you cancel the captcha, there will be no data from the query,
        // so only show a warning to the user if we have an actual uncaught error
        throw new Error(sendResponse.error.data?.message);
      }
      return false;
    }
  }
  if (!state?.details?.preventUpdateModal) await NotifyGuestsOverlayPromise(store.dispatch);
  return true;
};

export const editSteps = {
  meta: {
    // Not a step - global settings for steps
    editing: true,
    creating: false,
    showBackButton: false,
    countSteps: false,
    showBottomNav: false,
  },
  customize: {
    id: 1,
    name: 'customize',
    path: 'customize',
    displayName: 'Customize',
    title: `Customize Your Invitation`,
    linkPreview: true,
    nextRoute: (store) => {
      let newRoute;
      const {isPremium} = store.getState().details;
      const isPremiumTemplate = store.getState().fabricTemplate.premium;
      const {upsellConfirmed} = store.getState().modals;

      if (!isPremiumTemplate && isPremium && upsellConfirmed) {
        newRoute = 'review';
      } else if (isPremium) {
        newRoute = 'exit';
      } else {
        newRoute = 'confirm';
      }
      return newRoute;
    },
    preLeaveRouteFunction: async (store, routeName, canvas) => {
      await canvasRemoveTextBordersAndIcons(canvas);
      return store.dispatch(saveEventThunk());
    },
    nextButtonText: 'Done',
    hideInWebView: false,
  },
  details: {
    id: 2,
    name: 'details',
    path: 'details',
    displayName: 'Event Details',
    title: 'Enter Or Edit Your Event Details',
    linkPreview: true,
    nextRouteAvailableSelector: (state) => !selectIsDirty(state),
    // This is explicit because the param list of preLeaveRouteFunction and validateEventDetails are different
    preLeaveRouteFunction: (store) => validateEventDetails(store),
    nextRoute: (store) => {
      let newRoute;
      if (store.getState().details.isPremium) {
        newRoute = 'exit';
      } else {
        newRoute = 'confirm';
      }
      return newRoute;
    },
    nextButtonText: 'Done',
    hideInWebView: false,
  },
  gifting: {
    id: 3,
    path: 'gifting',
    name: 'gifting',
    displayName: 'Gifting Options',
    title: "Select one or more gift options you'd like to include in your invitation",
    linkPreview: true,
    nextRoute: (store) => {
      let newRoute;
      if (store.getState().details.isPremium) {
        newRoute = 'exit';
      } else {
        newRoute = 'confirm';
      }
      return newRoute;
    },
    nextButtonText: 'Done',
    hideInWebView: false,
  },
  preview: {
    id: 4,
    path: 'preview',
    name: 'preview',
    displayName: 'Preview',
    title: `This Is A Preview Of Your Invitation`,
    linkPreview: false,
    linkBack: true,
    nextButtonText: '',
    hideBottomNav: true,
    hideInWebView: false,
  },
  guests: {
    id: 5,
    path: 'guests',
    name: 'guests',
    displayName: 'Add Guests',
    title: 'Add guests',
    nextRoute: 'confirm',
    nextButtonText: (store) => {
      const state = store.getState();
      const hasSub = isSubscriptionPlan(state.user?.subscriptionPlanName, false);
      const isFreeEvent = !state.details.isPremium;
      return hasSub || window.eventHasProHost || isFreeEvent
        ? 'Send Invitation'
        : 'Next: Review & Send';
    },
    preLeaveRouteFunction: suppressGuestUpdateModal,
    hideInWebView: true,
  },
  review: {
    id: 6,
    path: 'review',
    name: 'review',
    displayName: 'Review & Send',
    title: 'Review & Send',
    nextRoute: 'confirm',
    nextButtonText: 'Done',
    hideInWebView: true,
  },
  confirm: {
    id: 7,
    path: 'confirm',
    name: 'confirm',
    displayName: 'Confirmation',
    title: 'Confirmation',
    previousRoute: 'review',
    saveOnLeave: false,
    hideInWebView: true,
    showMegaNav: true,
    showFooter: true,
    preEnterRouteFunction: saveAndSendGuestsMessage,
  },
  edit: {
    id: 8,
    path: 'edit',
    name: 'edit',
    displayName: 'Invitation Editor',
    title: 'Edit Your Invitation',
    linkPreview: false,
    nextButtonText: 'Save changes',
    nextRoute: 'send',
    hideInWebView: false,
    saveOnLeave: true,
  },
  send: {
    id: 9,
    path: !document.referrer?.includes?.('login') && document.referrer ? document.referrer : '/',
    name: 'send',
    hideInWebView: true,
    preEnterRouteFunction: saveAndSendGuestsMessage,
  },
  exit: {
    // Routes us to the source page
    id: -1,
    path: !document.referrer?.includes?.('login') && document.referrer ? document.referrer : '/',
    hideInWebView: true,
  },
};
